import { getSessionValue } from '../session_value_utils'

const ACTIVE = 'active'
const IDLE = 'idle'

const DEFAULT_INITIAL_STATE = ACTIVE

const DEFAULT_ACTIVITY_EVENTS = [
  'click',
  'mousemove',
  'keydown',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'focus'
]

const DEFAULT_INACTIVITY_EVENTS = []

const DEFAULT_IGNORED_EVENTS_WHEN_IDLE = ['mousemove']

// Cross-browser document visibility API detection
let hidden, visibilityChangeEvent
if (typeof document.hidden !== 'undefined') {
  hidden = 'hidden'
  visibilityChangeEvent = 'visibilitychange'
} else if (typeof document.msHidden !== 'undefined') {
  hidden = 'msHidden'
  visibilityChangeEvent = 'msvisibilitychange'
} else if (typeof document.webkitHidden !== 'undefined') {
  hidden = 'webkitHidden'
  visibilityChangeEvent = 'webkitvisibilitychange'
}

/**
 * Creates an activity detector instance with cross-tab coordination
 *
 * @param  {Object}   options
 * @param  {string[]} options.activityEvents        Events which force a transition to 'active'
 * @param  {string[]} options.inactivityEvents      Events which force a transition to 'idle'
 * @param  {string[]} options.ignoredEventsWhenIdle Events that are ignored in 'idle' state
 * @param  {number}   options.timeToIdle            Inactivity time in ms to transition to 'idle'
 * @param  {string}   options.initialState          One of 'active' or 'idle'
 * @param  {boolean}  options.autoInit
 * @param  {boolean}  options.syncBetweenTabs       Sync activity between tabs of same origin
 * @param  {string}   options.channelName           BroadcastChannel name for sync
 * @param  {string}   options.tabCountKey           localStorage key for tab counter
 * @return {Object}   activity detector instance
 */
const activityDetector = ({
  activityEvents = DEFAULT_ACTIVITY_EVENTS,
  inactivityEvents = DEFAULT_INACTIVITY_EVENTS,
  ignoredEventsWhenIdle = DEFAULT_IGNORED_EVENTS_WHEN_IDLE,
  timeToIdle = 30000,
  initialState = DEFAULT_INITIAL_STATE,
  autoInit = true,
  syncBetweenTabs = true,
  channelName = 'activity-detector-channel',
  tabCountKey = 'activity-detector-tab-count'
} = {}) => {
  const TS_KEY = tabCountKey + '-ts'

  // Internal listeners for ACTIVE and IDLE state changes
  const listeners = { [ACTIVE]: [], [IDLE]: [] }

  // Current state reference
  let state

  // Idle timeout handler
  let timer

  // Heartbeat interval to keep the timestamp fresh (guards against stale counts)
  let heartbeatInterval = null

  // BroadcastChannel instance for cross-tab sync
  let broadcastChannel = null

  // Unique identifier for this tab
  const tabId = generateTabId()

  // Tracks current tab visibility status
  let isTabVisible = true

  // Whether init() has been called and not yet stopped
  let initialized = false

  // Generates a unique ID for the tab session
  function generateTabId () {
    return 'tab_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  // Updates and returns current tab visibility state
  const updateTabVisibility = () => {
    isTabVisible = !hidden || !document[hidden]
    return isTabVisible
  }

  // Clears the inactivity timer safely
  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  // Tab counter helpers — scoped inside the factory to avoid conflicts
  // between multiple instances
  const incrementTabCount = () => {
    const current = parseInt(window.localStorage.getItem(tabCountKey) || '0', 10)
    const next = current + 1
    window.localStorage.setItem(tabCountKey, next)
    window.localStorage.setItem(TS_KEY, Date.now())
  }

  const decrementTabCount = () => {
    const current = parseInt(window.localStorage.getItem(tabCountKey) || '1', 10)
    const next = Math.max(0, current - 1)
    window.localStorage.setItem(tabCountKey, next)
    window.localStorage.setItem(TS_KEY, Date.now())
    return next
  }

  // If the last heartbeat timestamp is older than 30 min, the counter was
  // likely left dirty by a crash or forced kill — reset it before incrementing
  const sanitizeTabCount = () => {
    const lastTs = parseInt(window.localStorage.getItem(TS_KEY) || '0', 10)
    if (Date.now() - lastTs > 30 * 60 * 1000) {
      window.localStorage.setItem(tabCountKey, '0')
    }
  }

  // Centralized state change handler
  // "source" indicates if the change is local or coming from another tab
  const setState = (newState, source = 'local') => {
    clearTimer()

    // If we move to ACTIVE, start inactivity countdown (only when tab is visible)
    if (newState === ACTIVE) {
      if (isTabVisible) {
        timer = setTimeout(() => {
          if (syncBetweenTabs) {
            // Send a heartbeat before going idle so other tabs stay in sync
            broadcastActivityHeartbeat()
            setTimeout(() => {
              setState(IDLE, 'local')
            }, 100)
          } else {
            setState(IDLE, 'local')
          }
        }, timeToIdle)
      }
    }

    // Avoid emitting duplicate state changes
    if (state !== newState) {
      state = newState

      // Only notify listeners and broadcast when change is local
      if (source === 'local') {
        listeners[state].forEach(l => l())
        if (syncBetweenTabs && broadcastChannel) {
          broadcastChannel.postMessage({
            type: 'state-change',
            tabId,
            state,
            timestamp: Date.now()
          })
        }
      }
    }
  }

  // Returns current activity state
  const getState = () => state

  // Sends lightweight activity signal to other tabs
  const broadcastActivityHeartbeat = () => {
    if (broadcastChannel && syncBetweenTabs) {
      broadcastChannel.postMessage({
        type: 'heartbeat',
        tabId,
        state,
        timestamp: Date.now()
      })
    }
  }

  // Handles any user interaction event
  const handleUserActivityEvent = (event) => {
    // Ignore configured events while IDLE (for performance / noise filtering)
    if (state === ACTIVE || ignoredEventsWhenIdle.indexOf(event.type) < 0) {
      setState(ACTIVE, 'local')

      // Immediately notify other tabs about activity
      if (syncBetweenTabs) {
        broadcastActivityHeartbeat()
      }
    }
  }

  // Handles explicit inactivity events
  const handleUserInactivityEvent = () => {
    if (!syncBetweenTabs) {
      setState(IDLE, 'local')
    }
  }

  // Handles browser tab visibility changes
  const handleVisibilityChangeEvent = () => {
    const wasVisible = isTabVisible
    const nowVisible = updateTabVisibility()

    // When returning to the tab
    if (!wasVisible && nowVisible) {
      if (syncBetweenTabs) {
        broadcastChannel.postMessage({
          type: 'tab-visible',
          tabId,
          timestamp: Date.now()
        })
      }

      // If not syncing, restore ACTIVE state when user comes back
      if (!syncBetweenTabs && state === IDLE) {
        setState(ACTIVE, 'local')
      }

    // When leaving the tab
    } else if (wasVisible && !nowVisible) {
      if (!syncBetweenTabs) {
        clearTimer()
        timer = setTimeout(() => setState(IDLE, 'local'), timeToIdle)
      }
    }

    // Optional behavior: visibility change can directly control state
    if (inactivityEvents.indexOf('visibilitychange') >= 0) {
      setState(nowVisible ? ACTIVE : IDLE, 'local')
    }
  }

  // Handles incoming BroadcastChannel messages
  const handleBroadcastMessage = (event) => {
    if (!syncBetweenTabs) return

    const message = event.data

    switch (message.type) {
      case 'heartbeat':
        // Another tab is active, sync current tab to ACTIVE if needed
        if (state === IDLE && message.state === ACTIVE) {
          setState(ACTIVE, 'remote')
        } else if (state === ACTIVE) {
          // Reset idle timer when heartbeat arrives
          clearTimer()
          timer = setTimeout(() => setState(IDLE, 'local'), timeToIdle)
        }
        break

      case 'state-change':
        // Only react to ACTIVE transitions from other tabs
        if (message.state === ACTIVE) {
          setState(ACTIVE, 'remote')
        }
        break

      case 'tab-visible':
        // Reserved for future visibility sync logic
        break
      default:
        break
    }
  }

  // Initializes BroadcastChannel synchronization
  const initSync = () => {
    if (!syncBetweenTabs) return

    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannel = new BroadcastChannel(channelName)
      broadcastChannel.onmessage = handleBroadcastMessage

      // Notify other tabs that this tab is online
      broadcastChannel.postMessage({
        type: 'tab-visible',
        tabId,
        state: state || initialState,
        timestamp: Date.now()
      })
    }
  }

  // Shared close logic used by both beforeunload and pagehide
  // Only the first one to run takes effect (initialized guard)
  const handleTabClose = () => {
    if (!initialized) {
      return
    }
    initialized = false

    if (broadcastChannel && syncBetweenTabs) {
      broadcastChannel.postMessage({ type: 'tab-closing', tabId, timestamp: Date.now() })
    }

    const remaining = decrementTabCount()

    if (remaining === 0) {
      sendSessionEvent('/endpoints/token/tabsClose', getSessionValue())
    }
  }

  function sendSessionEvent (url, token) {
    const blob = new Blob([token], { type: 'application/json' })

    const ok = navigator.sendBeacon(url, blob)

    if (!ok) {
    // fallback
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: token,
        keepalive: true
      })
    }
  }

  let handleBeforeUnload
  let handlePageHide
  let handlePageShow

  /**
   * Starts the activity detector with the given state.
   * @param {string} firstState 'idle' or 'active'
   */
  const init = (firstState = DEFAULT_INITIAL_STATE) => {
    if (initialized) {
      return
    }
    initialized = true

    sanitizeTabCount()
    incrementTabCount()
    updateTabVisibility()

    // Initialize cross-tab communication first
    if (syncBetweenTabs) {
      initSync()
    }

    // Set initial state
    setState(firstState === ACTIVE ? ACTIVE : IDLE, 'local')

    // Register activity listeners
    activityEvents.forEach(eventName =>
      window.addEventListener(eventName, handleUserActivityEvent))

    // Register inactivity listeners (except visibilitychange, handled separately)
    inactivityEvents.filter(eventName => eventName !== 'visibilitychange')
      .forEach(eventName =>
        window.addEventListener(eventName, handleUserInactivityEvent))

    // Register visibility change listener if supported
    if (visibilityChangeEvent) {
      document.addEventListener(visibilityChangeEvent, handleVisibilityChangeEvent)
    }

    handlePageShow = () => {
      isTabVisible = true
    }

    // Page lifecycle handling (mobile/back-forward cache support)
    handlePageHide = (event) => {
      isTabVisible = false
      if (!event.persisted) {
        handleTabClose()
      }
    }

    // beforeunload fires on desktop browsers for hard closes and refreshes.
    // The initialized guard inside handleTabClose ensures only one of
    // beforeunload / pagehide actually runs the decrement+beacon logic.
    handleBeforeUnload = () => {
      handleTabClose()
    }

    // Refresh the timestamp every 5 min so the stale-count detector
    // doesn't reset a legitimately long-lived session
    heartbeatInterval = setInterval(() => {
      if (initialized) {
        window.localStorage.setItem(TS_KEY, Date.now())
      }
    }, 5 * 60 * 1000)

    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)
    window.addEventListener('beforeunload', handleBeforeUnload)
  }

  /**
   * Register an event listener for the required event
   * @param {string} eventName 'active' or 'idle'
   * @param {Function} listener
   */
  const on = (eventName, listener) => {
    if (listeners[eventName]) {
      listeners[eventName].push(listener)
    }

    // Returns unsubscribe function
    const off = () => {
      if (listeners[eventName]) {
        const index = listeners[eventName].indexOf(listener)
        if (index >= 0) {
          listeners[eventName].splice(index, 1)
        }
      }
    }
    return off
  }

  /**
   * Stops the activity detector and clean the listeners
   */
  const stop = () => {
    if (initialized) {
      initialized = false
      decrementTabCount()
    }

    clearTimer()

    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }

    listeners[ACTIVE] = []
    listeners[IDLE] = []

    // Remove all registered listeners
    activityEvents.forEach(eventName =>
      window.removeEventListener(eventName, handleUserActivityEvent))

    inactivityEvents.forEach(eventName =>
      window.removeEventListener(eventName, handleUserInactivityEvent))

    if (visibilityChangeEvent) {
      document.removeEventListener(visibilityChangeEvent, handleVisibilityChangeEvent)
    }

    if (broadcastChannel) {
      broadcastChannel.close()
      broadcastChannel = null
    }

    if (handlePageHide) {
      window.removeEventListener('pagehide', handlePageHide)
      handlePageHide = null
    }

    if (handlePageShow) {
      window.removeEventListener('pageshow', handlePageShow)
      handlePageShow = null
    }

    if (handleBeforeUnload) {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      handleBeforeUnload = null
    }
  }

  // Auto-start detector if enabled
  if (autoInit) {
    init(initialState)
  }

  return { on, stop, init, getState }
}

export default activityDetector
